import React, { useState, useEffect, useRef, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { AccessorialCodesTab } from '../components/AccessorialCodesTab';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { userAPI, customerAPI, carrierAPI, importAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { 
  Users, Shield, Plus, Pencil, Trash2, UserCheck, UserX, 
  Crown, ShieldCheck, Briefcase, Headphones, Building2, Truck,
  Link2, Search, LayoutDashboard, Package, MapPin, 
  BarChart3, Database, Settings as SettingsIcon, Sun, Moon, Monitor, DollarSign,
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, Loader2
} from 'lucide-react';

const ROLES = [
  { id: 'super_admin', label: 'Super Admin', icon: Crown, color: 'text-purple-500 bg-purple-500/10 border-purple-500/20', description: 'Full system access' },
  { id: 'admin', label: 'Admin', icon: ShieldCheck, color: 'text-blue-500 bg-blue-500/10 border-blue-500/20', description: 'Manage users & settings' },
  { id: 'account_manager', label: 'Account Manager', icon: Briefcase, color: 'text-green-500 bg-green-500/10 border-green-500/20', description: 'Manage customers & loads' },
  { id: 'operator', label: 'Operator', icon: Users, color: 'text-teal-500 bg-teal-500/10 border-teal-500/20', description: 'Full operational access' },
  { id: 'support_operator', label: 'Support Operator', icon: Headphones, color: 'text-orange-500 bg-orange-500/10 border-orange-500/20', description: 'Basic ops, no financials' },
  { id: 'customer', label: 'Customer', icon: Building2, color: 'text-[var(--tms-text-muted)] bg-[var(--tms-hover)]0/10 border-slate-500/20', description: 'View own loads only' },
  { id: 'carrier', label: 'Carrier', icon: Truck, color: 'text-red-500 bg-red-500/10 border-red-500/20', description: 'View assigned loads' },
];

const MODULES = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'loads', label: 'Loads', icon: Package },
  { id: 'customers', label: 'Customers', icon: Building2 },
  { id: 'carriers', label: 'Carriers', icon: Truck },
  { id: 'facilities', label: 'Facilities', icon: MapPin },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
  { id: 'reload', label: 'ReLoad', icon: Database },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
  { id: 'commission', label: 'Commission & Margin', icon: DollarSign },
];

const DEFAULT_PERMISSIONS = {
  super_admin: ['dashboard', 'loads', 'customers', 'carriers', 'facilities', 'reports', 'reload', 'settings', 'commission'],
  admin: ['dashboard', 'loads', 'customers', 'carriers', 'facilities', 'reports', 'reload', 'settings', 'commission'],
  account_manager: ['dashboard', 'loads', 'customers', 'carriers', 'facilities', 'reports', 'reload', 'commission'],
  operator: ['dashboard', 'loads', 'customers', 'carriers', 'facilities', 'reports', 'reload'],
  support_operator: ['dashboard', 'loads', 'carriers', 'facilities', 'reload'],
  customer: ['loads'],
  carrier: ['loads', 'reload'],
};

const RoleBadge = ({ role }) => {
  const roleInfo = ROLES.find(r => r.id === role) || ROLES[3];
  const Icon = roleInfo.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${roleInfo.color}`}>
      <Icon className="w-3 h-3" />
      {roleInfo.label}
    </span>
  );
};

const UserForm = ({ user, onSave, onCancel, currentUserRole, customers, carriers, accountManagers }) => {
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'operator',
    is_active: user?.is_active ?? true,
    linked_customer_id: user?.linked_customer_id || '',
    linked_carrier_id: user?.linked_carrier_id || '',
    manager_id: user?.manager_id || '',
    module_permissions: user?.module_permissions || DEFAULT_PERMISSIONS[user?.role || 'operator'] || [],
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...form };
    if (!data.password) delete data.password;
    if (data.role !== 'customer') delete data.linked_customer_id;
    if (data.role !== 'carrier') delete data.linked_carrier_id;
    // Only include manager_id for operators
    if (data.role !== 'operator') {
      delete data.manager_id;
    }
    onSave(data);
  };

  const handleRoleChange = (newRole) => {
    setForm({ 
      ...form, 
      role: newRole, 
      manager_id: '',
      module_permissions: DEFAULT_PERMISSIONS[newRole] || []
    });
  };

  const togglePermission = (moduleId) => {
    setForm(prev => {
      const current = prev.module_permissions || [];
      if (current.includes(moduleId)) {
        return { ...prev, module_permissions: current.filter(m => m !== moduleId) };
      } else {
        return { ...prev, module_permissions: [...current, moduleId] };
      }
    });
  };

  const resetToDefaults = () => {
    setForm(prev => ({
      ...prev,
      module_permissions: DEFAULT_PERMISSIONS[prev.role] || []
    }));
  };

  const availableRoles = ROLES.filter(r => {
    if (currentUserRole === 'super_admin') return true;
    if (r.id === 'super_admin') return false;
    return true;
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            data-testid="user-form-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            data-testid="user-form-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="password">{user ? 'New Password (leave blank to keep)' : 'Password *'}</Label>
          <Input
            id="password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required={!user}
            data-testid="user-form-password"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="role">Role *</Label>
          <Select value={form.role} onValueChange={handleRoleChange}>
            <SelectTrigger data-testid="user-form-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableRoles.map(r => (
                <SelectItem key={r.id} value={r.id}>
                  <div className="flex items-center gap-2">
                    <r.icon className="w-4 h-4" />
                    {r.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Assign Manager (for operator role) */}
      {form.role === 'operator' && (
        <div className="space-y-2 p-3 bg-teal-500/10 rounded-lg border border-teal-500/20">
          <Label htmlFor="manager_id" className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-teal-500" />
            Assign to Account Manager
          </Label>
          <Select value={form.manager_id || "none"} onValueChange={(val) => setForm({ ...form, manager_id: val === "none" ? "" : val })}>
            <SelectTrigger data-testid="user-form-manager">
              <SelectValue placeholder="Select account manager..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {accountManagers.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-teal-500">This operator will report to the selected account manager. The manager will see this operator's loads.</p>
        </div>
      )}

      {/* Link to Customer (for customer role) */}
      {form.role === 'customer' && (
        <div className="space-y-2 p-3 bg-[var(--tms-hover)] rounded-lg">
          <Label htmlFor="linked_customer">Link to Customer</Label>
          <Select value={form.linked_customer_id || "none"} onValueChange={(val) => setForm({ ...form, linked_customer_id: val === "none" ? "" : val })}>
            <SelectTrigger data-testid="user-form-customer-link">
              <SelectValue placeholder="Select customer..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-[var(--tms-text-muted)]">This user will only see loads for this customer</p>
        </div>
      )}

      {/* Link to Carrier (for carrier role) */}
      {form.role === 'carrier' && (
        <div className="space-y-2 p-3 bg-[var(--tms-hover)] rounded-lg">
          <Label htmlFor="linked_carrier">Link to Carrier</Label>
          <Select value={form.linked_carrier_id || "none"} onValueChange={(val) => setForm({ ...form, linked_carrier_id: val === "none" ? "" : val })}>
            <SelectTrigger data-testid="user-form-carrier-link">
              <SelectValue placeholder="Select carrier..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {carriers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-[var(--tms-text-muted)]">This user will only see loads assigned to this carrier</p>
        </div>
      )}

      {/* Module Access Permissions */}
      <div className="space-y-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2 text-indigo-700">
            <Shield className="w-4 h-4" />
            Module Access
          </Label>
          <button 
            type="button" 
            onClick={resetToDefaults}
            className="text-xs text-indigo-600 hover:text-indigo-800 underline"
          >
            Reset to defaults
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {MODULES.map(module => {
            const Icon = module.icon;
            const isChecked = form.module_permissions?.includes(module.id);
            return (
              <label 
                key={module.id} 
                className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                  isChecked ? 'bg-indigo-100 text-indigo-800' : 'bg-[var(--tms-card)] text-[var(--tms-text-secondary)] hover:bg-[var(--tms-hover)]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => togglePermission(module.id)}
                  className="rounded border-[var(--tms-border)] text-indigo-600"
                  data-testid={`module-perm-${module.id}`}
                />
                <Icon className="w-4 h-4" />
                <span className="text-sm">{module.label}</span>
              </label>
            );
          })}
        </div>
        <p className="text-xs text-indigo-600">Select which modules this user can access. Changes will take effect on their next login.</p>
      </div>

      {/* Active status (only for editing) */}
      {user && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="rounded border-[var(--tms-border)]"
          />
          <Label htmlFor="is_active" className="cursor-pointer">Active User</Label>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="user-form-submit">
          {user ? 'Update User' : 'Create User'}
        </Button>
      </div>
    </form>
  );
};

// Theme Settings Component
const ThemeSettings = () => {
  const { theme, setTheme, isDark } = useTheme();
  
  return (
    <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-6">
      <h3 className="text-lg font-semibold text-[var(--tms-text)] mb-4 flex items-center gap-2">
        <Monitor className="w-5 h-5" />
        Appearance Settings
      </h3>
      
      <div className="space-y-6">
        {/* Theme Selection */}
        <div>
          <Label className="text-[var(--tms-text)] mb-3 block">Theme Mode</Label>
          <div className="grid grid-cols-2 gap-4">
            {/* Light Mode Option */}
            <button
              onClick={() => setTheme('light')}
              className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                theme === 'light' 
                  ? 'border-[var(--tms-primary)] bg-[var(--tms-primary)]/10' 
                  : 'border-[var(--tms-border)] hover:border-[var(--tms-text-muted)]'
              }`}
              data-testid="theme-light"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center shadow-lg">
                  <Sun className="w-6 h-6 text-white" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-[var(--tms-text)]">Light Mode</p>
                  <p className="text-xs text-[var(--tms-text-muted)]">Best for bright environments</p>
                </div>
                {theme === 'light' && (
                  <span className="text-xs px-2 py-1 bg-[var(--tms-primary)] text-white rounded-full">Active</span>
                )}
              </div>
            </button>
            
            {/* Dark Mode Option */}
            <button
              onClick={() => setTheme('dark')}
              className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                theme === 'dark' 
                  ? 'border-[var(--tms-primary)] bg-[var(--tms-primary)]/10' 
                  : 'border-[var(--tms-border)] hover:border-[var(--tms-text-muted)]'
              }`}
              data-testid="theme-dark"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center shadow-lg">
                  <Moon className="w-6 h-6 text-white" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-[var(--tms-text)]">Dark Mode</p>
                  <p className="text-xs text-[var(--tms-text-muted)]">Reduces eye strain</p>
                </div>
                {theme === 'dark' && (
                  <span className="text-xs px-2 py-1 bg-[var(--tms-primary)] text-white rounded-full">Active</span>
                )}
              </div>
            </button>
          </div>
        </div>
        
        {/* Preview */}
        <div className="border-t border-[var(--tms-border)] pt-6">
          <Label className="text-[var(--tms-text)] mb-3 block">Preview</Label>
          <div className="p-4 rounded-lg bg-[var(--tms-bg)] border border-[var(--tms-border)]">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="h-3 w-24 bg-[var(--tms-text)] rounded mb-2 opacity-80"></div>
                <div className="h-2 w-32 bg-[var(--tms-text-secondary)] rounded opacity-60"></div>
              </div>
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded bg-[var(--tms-success)]"></div>
                <div className="w-8 h-8 rounded bg-[var(--tms-warning)]"></div>
                <div className="w-8 h-8 rounded bg-[var(--tms-error)]"></div>
                <div className="w-8 h-8 rounded bg-[var(--tms-primary)]"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Data Import Tab Component
const DataImportTab = () => {
  const [importing, setImporting] = useState({ carriers: false, customers: false, facilities: false });
  const [results, setResults] = useState({ carriers: null, customers: null, facilities: null });
  const carrierFileRef = useRef(null);
  const customerFileRef = useRef(null);
  const facilityFileRef = useRef(null);

  const downloadTemplate = async (type) => {
    try {
      let response;
      let filename;
      
      switch (type) {
        case 'carriers':
          response = await importAPI.downloadCarrierTemplate();
          filename = 'carrier_import_template.xlsx';
          break;
        case 'customers':
          response = await importAPI.downloadCustomerTemplate();
          filename = 'customer_import_template.xlsx';
          break;
        case 'facilities':
          response = await importAPI.downloadFacilityTemplate();
          filename = 'facility_import_template.xlsx';
          break;
        default:
          return;
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} template downloaded`);
    } catch (error) {
      toast.error(`Failed to download template: ${error.message}`);
    }
  };

  const handleFileUpload = async (type, file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setImporting(prev => ({ ...prev, [type]: true }));
    setResults(prev => ({ ...prev, [type]: null }));

    try {
      let response;
      switch (type) {
        case 'carriers':
          response = await importAPI.importCarriers(file);
          break;
        case 'customers':
          response = await importAPI.importCustomers(file);
          break;
        case 'facilities':
          response = await importAPI.importFacilities(file);
          break;
        default:
          return;
      }

      setResults(prev => ({ ...prev, [type]: response.data }));
      
      if (response.data.success > 0) {
        toast.success(`Successfully imported ${response.data.success} ${type}`);
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} ${type} failed to import`);
      }
    } catch (error) {
      toast.error(`Import failed: ${error.response?.data?.detail || error.message}`);
      setResults(prev => ({ ...prev, [type]: { success: 0, failed: 0, errors: [{ row: 0, error: error.message }] } }));
    } finally {
      setImporting(prev => ({ ...prev, [type]: false }));
    }
  };

  const ImportCard = ({ type, title, icon: Icon, description, fields }) => {
    const fileRef = type === 'carriers' ? carrierFileRef : type === 'customers' ? customerFileRef : facilityFileRef;
    const isImporting = importing[type];
    const result = results[type];

    return (
      <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-6 flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-[var(--tms-primary)]/10">
            <Icon className="w-6 h-6 text-[var(--tms-primary)]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[var(--tms-text)]">{title}</h3>
            <p className="text-sm text-[var(--tms-text-muted)]">{description}</p>
          </div>
        </div>

        {/* Required Fields */}
        <div className="mb-4 p-3 bg-[var(--tms-hover)] rounded-lg">
          <p className="text-xs font-medium text-[var(--tms-text-secondary)] mb-2">Required Fields:</p>
          <div className="flex flex-wrap gap-2">
            {fields.map((field) => (
              <span key={field} className="text-xs px-2 py-1 bg-[var(--tms-card)] border border-[var(--tms-border)] rounded text-[var(--tms-text)]">
                {field}
              </span>
            ))}
          </div>
        </div>

        {/* Actions - pushed to bottom */}
        <div className="flex gap-2 mt-auto">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 border-[var(--tms-border)] text-[var(--tms-text)] hover:bg-[var(--tms-hover)]"
            onClick={() => downloadTemplate(type)}
          >
            <Download className="w-4 h-4 mr-1" />
            Template
          </Button>
          
          <input
            type="file"
            ref={fileRef}
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFileUpload(type, e.target.files[0])}
          />
          
          <Button
            size="sm"
            className="flex-1 bg-[var(--tms-primary)] hover:opacity-90 text-white"
            onClick={() => fileRef.current?.click()}
            disabled={isImporting}
          >
            {isImporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-1" />
                Upload Excel
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div className="border-t border-[var(--tms-border)] pt-4">
            <div className="flex items-center gap-4 mb-3">
              {result.success > 0 && (
                <div className="flex items-center gap-2 text-[var(--tms-success)]">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{result.success} imported</span>
                </div>
              )}
              {result.failed > 0 && (
                <div className="flex items-center gap-2 text-[var(--tms-error)]">
                  <XCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{result.failed} failed</span>
                </div>
              )}
            </div>
            
            {result.errors && result.errors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium text-red-400 mb-2">Errors:</p>
                {result.errors.map((err, idx) => (
                  <div key={`${err.row}-${idx}`} className="text-xs text-red-300 mb-1">
                    <span className="font-mono">Row {err.row}:</span> {err.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <FileSpreadsheet className="w-6 h-6 text-[var(--tms-primary)]" />
          <div>
            <h2 className="text-lg font-semibold text-[var(--tms-text)]">Bulk Data Import</h2>
            <p className="text-sm text-[var(--tms-text-muted)]">
              Import carriers, customers, and facilities from Excel files
            </p>
          </div>
        </div>
        
        <div className="p-4 bg-[var(--tms-hover)] rounded-lg border border-[var(--tms-border)]">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[var(--tms-warning)] mt-0.5" />
            <div className="text-sm text-[var(--tms-text-secondary)]">
              <p className="font-medium text-[var(--tms-text)] mb-1">How to use:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Download the template for the data type you want to import</li>
                <li>Fill in your data following the template format (delete sample rows first)</li>
                <li>Save the file as .xlsx format</li>
                <li>Upload the file using the "Upload Excel" button</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ImportCard
          type="carriers"
          title="Import Carriers"
          icon={Truck}
          description="Bulk import carrier/trucking company data"
          fields={['Name', 'MC#', 'Classification']}
        />
        
        <ImportCard
          type="customers"
          title="Import Customers"
          icon={Building2}
          description="Bulk import customer/shipper data"
          fields={['Name', 'Contact Name']}
        />
        
        <ImportCard
          type="facilities"
          title="Import Facilities"
          icon={MapPin}
          description="Bulk import pickup/delivery locations"
          fields={['Name', 'Address', 'City', 'State', 'Zip']}
        />
      </div>
    </div>
  );
};

const Settings = () => {
  const { user: currentUser, isSuperAdmin, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, customersRes, carriersRes] = await Promise.all([
        userAPI.getAll(),
        customerAPI.getAll(),
        carrierAPI.getAll()
      ]);
      setUsers(usersRes.data);
      setCustomers(customersRes.data);
      setCarriers(carriersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateUser = async (data) => {
    try {
      await userAPI.create(data);
      toast.success('User created successfully');
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (data) => {
    try {
      await userAPI.update(editingUser.id, data);
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await userAPI.delete(userId);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await userAPI.update(user.id, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const usersByRole = ROLES.reduce((acc, role) => {
    acc[role.id] = users.filter(u => u.role === role.id).length;
    return acc;
  }, {});

  if (!isAdmin()) {
    return (
      <MainLayout>
        <div className="p-8 text-center">
          <Shield className="w-16 h-16 mx-auto text-[var(--tms-text-muted)] mb-4" />
          <h1 className="text-2xl font-bold text-[var(--tms-text)]">Access Denied</h1>
          <p className="text-[var(--tms-text-secondary)] mt-2">You don't have permission to access Settings.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" data-testid="settings-page">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--tms-text)]">Settings</h1>
          <p className="text-sm md:text-base text-[var(--tms-text-secondary)] mt-1">Manage users and system configuration</p>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="flex flex-wrap bg-[var(--tms-card)] border border-[var(--tms-border)]">
            <TabsTrigger value="users" className="flex items-center gap-2 data-[state=active]:bg-[var(--tms-primary)] data-[state=active]:text-white">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="import" className="flex items-center gap-2 data-[state=active]:bg-[var(--tms-primary)] data-[state=active]:text-white">
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Data Import</span>
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2 data-[state=active]:bg-[var(--tms-primary)] data-[state=active]:text-white">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Role Reference</span>
            </TabsTrigger>
            <TabsTrigger value="accessorials" className="flex items-center gap-2 data-[state=active]:bg-[var(--tms-primary)] data-[state=active]:text-white" data-testid="settings-tab-accessorials">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Accessorials</span>
            </TabsTrigger>
            {/* DARK MODE DISABLED - Appearance tab hidden
            <TabsTrigger value="appearance" className="flex items-center gap-2 data-[state=active]:bg-[var(--tms-primary)] data-[state=active]:text-white">
              <Monitor className="w-4 h-4" />
              <span className="hidden sm:inline">Appearance</span>
            </TabsTrigger>
            */}
          </TabsList>

          {/* DARK MODE DISABLED - Appearance Tab hidden
          <TabsContent value="appearance">
            <ThemeSettings />
          </TabsContent>
          */}

          {/* Data Import Tab */}
          <TabsContent value="import">
            <DataImportTab />
          </TabsContent>

          {/* Accessorial Codes Tab */}
          <TabsContent value="accessorials">
            <AccessorialCodesTab />
          </TabsContent>

          <TabsContent value="users">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
              {ROLES.map(role => (
                <div key={role.id} className={`p-3 rounded-lg border ${role.color}`}>
                  <div className="flex items-center gap-2">
                    <role.icon className="w-4 h-4" />
                    <span className="text-xs font-medium truncate">{role.label}</span>
                  </div>
                  <p className="text-2xl font-bold mt-1 text-[var(--tms-text)]">{usersByRole[role.id] || 0}</p>
                </div>
              ))}
            </div>

            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--tms-text-muted)]" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-[var(--tms-card)] border-[var(--tms-border)] text-[var(--tms-text)]"
                  data-testid="search-users"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-48 bg-[var(--tms-card)] border-[var(--tms-border)] text-[var(--tms-text)]" data-testid="filter-role">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {ROLES.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-user-button" className="w-full sm:w-auto">
                    <Plus className="w-4 h-4 mr-2" /> Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <UserForm
                    onSave={handleCreateUser}
                    onCancel={() => setShowDialog(false)}
                    currentUserRole={currentUser?.role}
                    customers={customers}
                    carriers={carriers}
                    accountManagers={users.filter(u => u.role === 'account_manager')}
                  />
                </DialogContent>
              </Dialog>
            </div>

            {/* Users Table */}
            <div className="bg-[var(--tms-card)] border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--tms-hover)] border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[var(--tms-text-secondary)]">User</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[var(--tms-text-secondary)] hidden md:table-cell">Email</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[var(--tms-text-secondary)]">Role</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[var(--tms-text-secondary)] hidden lg:table-cell">Manager</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-[var(--tms-text-secondary)]">Status</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-[var(--tms-text-secondary)]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-[var(--tms-text-muted)]">Loading...</td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-[var(--tms-text-muted)]">No users found</td>
                      </tr>
                    ) : (
                      filteredUsers.map(user => (
                        <tr key={user.id} className="hover:bg-[var(--tms-hover)]" data-testid={`user-row-${user.id}`}>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-[var(--tms-text)]">{user.name}</p>
                              <p className="text-xs text-[var(--tms-text-muted)] md:hidden">{user.email}</p>
                              {user.manager_name && (
                                <p className="text-xs text-teal-600 lg:hidden">→ {user.manager_name}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[var(--tms-text-secondary)] hidden md:table-cell">{user.email}</td>
                          <td className="px-4 py-3">
                            <RoleBadge role={user.role} />
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {user.manager_name ? (
                              <span className="text-sm text-teal-700 flex items-center gap-1">
                                <Link2 className="w-3 h-3" />
                                {user.manager_name}
                              </span>
                            ) : user.role === 'operator' ? (
                              <span className="text-xs text-[var(--tms-text-muted)]">Not assigned</span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {user.is_active ? (
                              <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                                <UserCheck className="w-4 h-4" /> Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[var(--tms-text-muted)] text-xs">
                                <UserX className="w-4 h-4" /> Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingUser(user)}
                                disabled={user.role === 'super_admin' && !isSuperAdmin()}
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(user)}
                                disabled={user.id === currentUser?.id || (user.role === 'super_admin' && !isSuperAdmin())}
                              >
                                {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                              </Button>
                              {isSuperAdmin() && user.id !== currentUser?.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteUser(user.id)}
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Edit User Dialog */}
            <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit User: {editingUser?.name}</DialogTitle>
                </DialogHeader>
                {editingUser && (
                  <UserForm
                    user={editingUser}
                    onSave={handleUpdateUser}
                    onCancel={() => setEditingUser(null)}
                    currentUserRole={currentUser?.role}
                    customers={customers}
                    carriers={carriers}
                    accountManagers={users.filter(u => u.role === 'account_manager')}
                  />
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="roles">
            <div className="bg-[var(--tms-card)] border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Role Permissions Reference</h3>
              <div className="grid gap-4">
                {ROLES.map(role => (
                  <div key={role.id} className="border rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${role.color}`}>
                        <role.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{role.label}</h4>
                        <p className="text-sm text-[var(--tms-text-muted)]">{role.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      {role.id === 'super_admin' && (
                        <>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ All Access</span>
                        </>
                      )}
                      {role.id === 'admin' && (
                        <>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Dashboard</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Loads</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Customers</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Carriers</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Facilities</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Reports</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ ReLoad</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Settings</span>
                        </>
                      )}
                      {(role.id === 'account_manager' || role.id === 'operator') && (
                        <>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Dashboard</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Loads</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Customers</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Carriers</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Facilities</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Own Reports</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ ReLoad</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Own Commission</span>
                        </>
                      )}
                      {role.id === 'support_operator' && (
                        <>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Dashboard</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Loads</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Customers</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Carriers</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Facilities</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ ReLoad</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded">✗ No Reports</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded">✗ No Commission</span>
                        </>
                      )}
                      {role.id === 'customer' && (
                        <>
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">⚠ Own Dashboard</span>
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">⚠ Own Loads</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded">✗ No Carriers</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded">✗ No Financials</span>
                        </>
                      )}
                      {role.id === 'carrier' && (
                        <>
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">⚠ Own Dashboard</span>
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">⚠ Assigned Loads</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ ReLoad</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded">✗ No Customers</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default Settings;
